/**
 * OmniTrace Analytics — Dual-tracking with Google Analytics 4 + Mixpanel
 *
 * This module is the single source of truth for all analytics tracking across
 * the tenant web app. Every user interaction, feature usage, and conversion
 * event flows through here to both GA4 and Mixpanel.
 *
 * Architecture:
 *   analytics.init()          — once at startup (main.tsx)
 *   analytics.identify(user)  — on login / session restore
 *   analytics.reset()         — on logout
 *   analytics.track(event, props)  — anywhere
 *   analytics.setTenantGroup(id, traits) — on login (after identify)
 *
 * Event Naming Convention: object.action snake_case (e.g., "device.registered", "trip.playback_started")
 * Properties: snake_case keys (e.g., device_id, report_type)
 */
import mixpanel, { type Dict } from 'mixpanel-browser'

// ── Types ──────────────────────────────────────────────────────────

interface UserProfile {
  id: string
  name: string
  email: string
  tenant_id: string
  roles?: string[]
  plan?: string
}

type EventProps = Record<string, string | number | boolean | string[] | null | undefined>

// ── GA4 helpers (gtag.js) ──────────────────────────────────────────

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

function ga4Event(name: string, params?: EventProps): void {
  if (typeof window.gtag !== 'function') return
  window.gtag('event', name, params)
}

function ga4SetUser(userId: string, props?: EventProps): void {
  if (typeof window.gtag !== 'function') return
  window.gtag('config', GA4_ID, { user_id: userId })
  if (props) window.gtag('set', 'user_properties', props)
}

function ga4PageView(path: string, title: string): void {
  if (typeof window.gtag !== 'function') return
  window.gtag('event', 'page_view', { page_path: path, page_title: title })
}

// ── Constants ──────────────────────────────────────────────────────

const GA4_ID = (import.meta.env.VITE_GA4_MEASUREMENT_ID as string) || 'G-B25NENHDDW'
let mpEnabled = false

// ── Event Taxonomy ─────────────────────────────────────────────────
// Centralized event names prevent typos and enable autocomplete.

export const EVENTS = {
  // Lifecycle
  SESSION_STARTED: 'session.started',
  SESSION_ENDED: 'session.ended',
  SESSION_LOGIN_FAILED: 'session.login_failed',
  SESSION_REVOKED: 'session.revoked',
  TWO_FA_VERIFIED: 'two_fa.verified',
  TWO_FA_SETUP_COMPLETED: 'two_fa.setup_completed',

  // Core Value
  MAP_VIEWED: 'map.viewed',
  MAP_DEVICE_SELECTED: 'map.device_selected',
  DEVICE_REGISTERED: 'device.registered',
  DEVICE_ASSIGNED: 'device.assigned',
  DEVICE_UNASSIGNED: 'device.unassigned',
  COMMAND_SENT: 'command.sent',
  TRIP_PLAYBACK_STARTED: 'trip.playback_started',
  TRIP_COMPARED: 'trip.compared',
  RULE_CREATED: 'rule.created',
  ALERT_ACKNOWLEDGED: 'alert.acknowledged',
  GEOFENCE_CREATED: 'geofence.created',
  JOB_CREATED: 'job.created',

  // Fleet Operations
  JOB_STATUS_CHANGED: 'job.status_changed',
  MAINTENANCE_CREATED: 'maintenance.created',
  MAINTENANCE_COMPLETED: 'maintenance.completed',
  REPORT_GENERATED: 'report.generated',
  SHARE_LINK_CREATED: 'share_link.created',

  // Collaboration
  TRUST_CIRCLE_DEVICE_SHARED: 'trust_circle.device_shared',
  TRUST_CIRCLE_REVOKED: 'trust_circle.revoked',

  // Configuration
  DEVICE_UPDATED: 'device.updated',
  DEVICE_DELETED: 'device.deleted',
  DEVICE_GROUP_CREATED: 'device_group.created',
  RULE_TOGGLED: 'rule.toggled',
  USER_INVITED: 'user.invited',
  ROLE_UPDATED: 'role.updated',

  // Billing
  BILLING_VIEWED: 'billing.viewed',
  PAYMENT_INITIATED: 'payment.initiated',
  PLAN_SELECTED: 'plan.selected',

  // Notifications
  NOTIFICATION_SENT: 'notification.sent',

  // Settings
  SETTINGS_LANGUAGE_CHANGED: 'settings.language_changed',
  SETTINGS_THEME_CHANGED: 'settings.theme_changed',
  ANALYTICS_OPT_CHANGED: 'analytics.opt_changed',
} as const

export type EventName = (typeof EVENTS)[keyof typeof EVENTS]

// ── Core Analytics API ─────────────────────────────────────────────

export const analytics = {
  /**
   * Initialize both Mixpanel and GA4. Call once at app startup.
   */
  init(): void {
    // Mixpanel
    const mpToken = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined
    if (mpToken) {
      const env = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE
      mixpanel.init(mpToken, {
        debug: env !== 'production',
        track_pageview: false, // we handle page views manually for richer data
        persistence: 'localStorage',
        ip: true,
        api_host: 'https://api-eu.mixpanel.com',
        autocapture: true,
        record_sessions_percent: 100,
        ignore_dnt: true,
      } as any)
      mpEnabled = true
    }

    // GA4 is loaded via script tag in index.html — just verify it's available
    if (typeof window.gtag !== 'function') {
      window.dataLayer = window.dataLayer || []
      window.gtag = function gtag() {
        window.dataLayer.push(arguments)
      }
    }
  },

  /**
   * Identify the logged-in user. Sets user properties on both platforms.
   */
  identify(user: UserProfile): void {
    const userProps = {
      tenant_id: user.tenant_id,
      roles: (user.roles ?? []).join(', '),
      plan: user.plan ?? 'unknown',
      platform: 'web' as const,
      app: 'tenant' as const,
    }

    // Mixpanel
    if (mpEnabled) {
      mixpanel.identify(user.id)
      mixpanel.people.set({
        $name: user.name,
        $email: user.email,
        ...userProps,
        $last_login: new Date().toISOString(),
      })
      mixpanel.register(userProps)
    }

    // GA4
    ga4SetUser(user.id, userProps)

    // Mixpanel Group Analytics — associate user with their tenant
    if (mpEnabled && user.tenant_id) {
      mixpanel.set_group('tenant_id', user.tenant_id)
    }
  },

  /**
   * Set tenant group profile traits in Mixpanel Group Analytics.
   * Call on login (after identify) and when tenant traits change.
   */
  setTenantGroup(tenantId: string, traits?: {
    name?: string
    plan?: string
    subscription_status?: string
    device_count?: number
    user_count?: number
    mrr?: number
  }): void {
    if (!mpEnabled) return
    mixpanel.set_group('tenant_id', tenantId)
    if (traits) {
      mixpanel.get_group('tenant_id', tenantId).set(traits)
    }
  },

  /**
   * Reset identity on logout.
   */
  reset(): void {
    if (mpEnabled) mixpanel.reset()
    // GA4 doesn't have a reset — the next page load starts a new session
  },

  /**
   * Track a named event with optional properties.
   * Sent to both Mixpanel and GA4.
   */
  track(event: EventName | string, props?: EventProps): void {
    // Mixpanel
    if (mpEnabled) {
      mixpanel.track(event, props as Dict)
    }

    // GA4 — convert dots to underscores for GA4 convention
    const ga4Name = event
      .replace(/\./g, '_')
      .substring(0, 40)
    ga4Event(ga4Name, props)
  },

  /**
   * Track timing events (e.g., time to load, time to interact).
   */
  timing(category: string, variable: string, valueMs: number): void {
    if (mpEnabled) {
      mixpanel.track('Performance', {
        category,
        variable,
        duration_ms: valueMs,
      })
    }
    ga4Event('timing_complete', {
      event_category: category,
      name: variable,
      value: Math.round(valueMs),
    })
  },

  /**
   * Increment a numeric user property (e.g., login_count, devices_created).
   */
  increment(property: string, by: number = 1): void {
    if (mpEnabled) {
      mixpanel.people.increment(property, by)
    }
  },

  /**
   * Set a user property once (only if not already set).
   * Useful for first_login_date, first_device_added, etc.
   */
  setOnce(props: EventProps): void {
    if (mpEnabled) {
      mixpanel.people.set_once(props as Dict)
    }
  },

  /**
   * Track revenue event for Mixpanel Revenue analytics.
   */
  revenue(amount: number, planName: string): void {
    if (mpEnabled) {
      mixpanel.people.track_charge(amount, { plan: planName })
    }
    ga4Event('purchase', {
      value: amount,
      currency: 'USD',
      items: planName,
    })
  },

  /**
   * Start a timed event. Call track() with the same event name to end it.
   * Mixpanel will automatically add $duration property.
   */
  timeEvent(event: EventName | string): void {
    if (mpEnabled) {
      mixpanel.time_event(event)
    }
  },
}
